import { z } from "zod";
import { entityId, MAX_FILTER_VALUES } from "./catalog";
import { RATING_MAX_STARS, RATING_MIN_STARS } from "./ratings";
import { validateSessionDates } from "./reading";
import { languageListSchema, languageTagSchema, type MetadataProviderId } from "./settings";

/*
 * Author suggestions contract (add-author-suggestions): identity sources, DTOs, query and decision
 * schemas, job constants and limits. Frozen after the foundation workstream except for additive,
 * backwards-compatible fields.
 */

// --- sources and statuses -------------------------------------------------------------

export const IDENTITY_SOURCES = ["openlibrary", "dnb"] as const;
export type IdentitySource = (typeof IDENTITY_SOURCES)[number];

export const WORK_SOURCES = [...IDENTITY_SOURCES, "googlebooks"] as const;
export type WorkSource = (typeof WORK_SOURCES)[number];

/** `pending` is never stored: it is the state of an author without a mapping row. */
export const MAPPING_STATUSES = ["pending", "auto", "unclear", "none", "manual"] as const;
export type MappingStatus = (typeof MAPPING_STATUSES)[number];

/** `suggestion_accepted`: a manual mapping created by accepting a mapping suggestion. */
export const MAPPING_ORIGINS = ["admin", "suggestion_accepted"] as const;
export type MappingOrigin = (typeof MAPPING_ORIGINS)[number];

/** Outcome of the last completed check of an author. */
export const SUGGESTION_OUTCOMES = ["ok", "unclear", "none", "excluded", "failed"] as const;
export type SuggestionOutcome = (typeof SUGGESTION_OUTCOMES)[number];

export const REFRESH_STATES = ["idle", "queued", "running"] as const;
export type RefreshState = (typeof REFRESH_STATES)[number];

export const SUGGESTION_BADGES = ["new_release", "series_gap"] as const;
export type SuggestionBadge = (typeof SUGGESTION_BADGES)[number];

/** Open Library author id, for example `OL26320A`. */
export const OL_AUTHOR_ID = /^OL\d{1,12}A$/;
/** GND person id: 1 to 10 digits, optional hyphen, check character (digit or `X`). */
export const GND_ID = /^\d{1,10}-?[\dX]$/;

export function isValidExternalAuthorId(source: IdentitySource, id: string): boolean {
  return (source === "openlibrary" ? OL_AUTHOR_ID : GND_ID).test(id);
}

/** Page of an external author at its source (shown as a link in the admin UI). */
export function externalAuthorUrl(source: IdentitySource, id: string): string {
  return source === "openlibrary"
    ? `https://openlibrary.org/authors/${id}`
    : `https://d-nb.info/gnd/${id}`;
}

// --- jobs and limits ---------------------------------------------------------------------

export const AUTHOR_SUGGESTIONS_REFRESH_JOB = "author_suggestions_refresh";
export const AUTHOR_SUGGESTIONS_SWEEP_JOB = "author_suggestions_sweep";
/** Mapping-only job: identifies authors without a confirmed id and stores scored candidates. */
export const AUTHOR_MAPPING_AUTOMAP_JOB = "author_mapping_automap";

/** One job that maps (non-manual sources) and collects the works of every author that needs it. */
export const AUTHOR_SUGGESTIONS_SYNC_JOB = "author_suggestions_sync";

export const REFRESH_JOB_PRIORITY = 10;
export const SWEEP_JOB_PRIORITY = 0;
/** Legacy: no code submits `author_mapping_automap` any more; queued jobs from before the upgrade still run. */
export const AUTOMAP_JOB_PRIORITY = 5;
/** Same slot as the former automap job: between the refresh (10) and the sweep (0). */
export const SYNC_JOB_PRIORITY = 5;

export interface RefreshJobPayload {
  authorId: string;
}
export interface SweepJobPayload {
  limit: number;
}

export interface RefreshJobResult {
  outcome: SuggestionOutcome;
  /** Suggestions stored for the author after the check. */
  suggestions: number;
}
export interface SweepJobResult {
  checkedAuthors: number;
  failedAuthors: number;
  stoppedBy?: RunStoppedBy;
}

/** Why a sync or sweep run ended before every selected author was checked. */
export type RunStoppedBy = "priority" | "rate_limit" | "cancelled" | "time_box";

export interface SyncJobPayload {
  /** `true`: also authors that are not due ("re-check all"). */
  all: boolean;
  /** ISO time of the request: after a restart the run only takes authors not checked since then. */
  requestedAt: string;
  /** Authors selected when the job was requested (informational). */
  authors: number;
}
export interface SyncJobResult {
  checkedAuthors: number;
  /** Mappings that became `auto` during the run. */
  acceptedMappings: number;
  /** Open mapping suggestions (`unclear` with a usable candidate) created during the run. */
  suggestedMappings: number;
  authorsWithNewSuggestions: number;
  /** Suggestions stored for the checked authors at the end of their checks. */
  suggestionsFound: number;
  failedAuthors: number;
  stoppedBy?: RunStoppedBy;
}
/** Running totals written to `JobProgressDto.totals` (the keys of {@link SyncJobResult} that are counts). */
export type SyncTotals = Pick<
  SyncJobResult,
  | "checkedAuthors"
  | "acceptedMappings"
  | "suggestedMappings"
  | "authorsWithNewSuggestions"
  | "suggestionsFound"
  | "failedAuthors"
>;

export interface AutomapJobPayload {
  /** Number of authors selected when the job was requested (informational). */
  authors: number;
  /** ISO time of the request: after an interruption the run skips sources decided since then. */
  startedAt?: string;
}
export interface AutomapJobResult {
  checkedAuthors: number;
  /** Mappings accepted by the automatic rule. */
  acceptedMappings: number;
  /** Mappings left `unclear` with at least one open suggestion. */
  suggestedMappings: number;
  failedAuthors: number;
  stoppedBy?: "priority" | "rate_limit" | "cancelled";
}

export const DAY_MS = 24 * 60 * 60 * 1000;
export const SUGGESTION_RECHECK_DAYS = 30;
export const SUGGESTION_RETRY_DAYS = 1;
/** Upper bound of authors per sweep run. */
export const SWEEP_MAX_AUTHORS = 60;
export const SWEEP_FIRST_TICK_MS = 10 * 60 * 1000;
/** Scheduler tick: due authors are picked up within half an hour. */
export const SWEEP_TICK_INTERVAL_MS = 30 * 60 * 1000;
/** A sweep run stops before the next author once it has run this long. */
export const SWEEP_MAX_RUN_MS = 20 * 60 * 1000;
/** Authors the sweep checks per rolling 24 hours (admin syncs and refreshes do not count). */
export const SWEEP_DAILY_AUTHOR_CAP = 300;
/** After an Open Library or DNB quota error no sweep is enqueued for this long. */
export const PROVIDER_QUOTA_PAUSE_MS = 60 * 60 * 1000;
/** Per-author time estimate used in the sync confirmation ("count x 25 s"). */
export const SYNC_SECONDS_PER_AUTHOR_ESTIMATE = 25;
/** The ETA of a running sync is only reported after this many checked authors. */
export const SYNC_ETA_MIN_AUTHORS = 3;
/** The status reports the last finished sync of this many days. */
export const SYNC_LAST_FINISHED_DAYS = 7;
/** Polling interval of the sync panel while a sync or sweep is queued or running, and otherwise. */
export const SYNC_POLL_ACTIVE_MS = 5000;
export const SYNC_POLL_IDLE_MS = 60_000;
export const REFRESH_COOLDOWN_MS = 10 * 60 * 1000;
export const REFRESH_LIMIT_PER_HOUR = 10;
export const MAX_MAPPING_IDS = 3;
export const MAX_CANDIDATES_PER_SOURCE = 3;
export const MAX_OVERLAP_TITLES = 5;
export const MAX_SUGGESTIONS_PER_AUTHOR = 100;
/** Requests per author check and the minimum gap between two requests to one provider. */
/** Default request budget of the pacing helpers; real checks use the per-provider budgets below. */
export const AUTHOR_CHECK_REQUEST_BUDGET = 25;
/**
 * Requests one author check may send per provider (each provider has its own budget, so one cannot
 * starve the next). Sized from the real request pattern: Open Library up to 3 ISBN books (edition,
 * optional work: 6), up to 9 distinct author records (cached per check), 1 name search and
 * `worksOf` for 3 candidates (2 languages x 2 pages: 12) = 28; DNB 3 ISBN lookups, 1 name search
 * and 3 x 2 work pages = 10 (12 for a manual mapping with 3 ids = 16 with the lookups); Google Books 1.
 */
export const AUTHOR_CHECK_REQUEST_BUDGETS = { openlibrary: 32, dnb: 16, googlebooks: 2 } as const;

/** Author names that stand for "nobody": never looked up, status `none` (normalized, see below). */
export const PLACEHOLDER_AUTHOR_NAMES = [
  "unknown",
  "unknown author",
  "unknown authors",
  "anonymous",
  "anonym",
  "unbekannt",
  "unbekannter autor",
  "various",
  "various authors",
  "verschiedene",
  "verschiedene autoren",
  "diverse",
  "diverse autoren",
  "n n",
  "n a",
] as const;

const placeholderKey = (name: string): string =>
  name
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

/** True for placeholder names such as "Unknown", "[Unbekannt]" or "Various"; `extra` extends the built-in list. */
export function isPlaceholderAuthorName(name: string, extra: readonly string[] = []): boolean {
  const key = placeholderKey(name);
  return [...PLACEHOLDER_AUTHOR_NAMES, ...extra].some((entry) => placeholderKey(entry) === key);
}
export const AUTHOR_CHECK_TIMEOUT_MS = 120_000;
export const PROVIDER_MIN_INTERVAL_MS = 1000;
/** Candidates scoring below this are stored but not offered as a mapping suggestion. */
export const MAPPING_SUGGESTION_MIN_SCORE = 20;
/** Default confidence for "accept all suggestions with confidence of at least X". */
export const MAPPING_ACCEPT_DEFAULT_CONFIDENCE = 80;
/** Months a release counts as "new" after its publication date. */
export const NEW_RELEASE_MONTHS = 12;
export const SUGGESTION_PAGE_SIZE_DEFAULT = 24;
export const SUGGESTION_PAGE_SIZE_MAX = 100;
export const SUGGESTION_STRIP_SIZE = 12;
export const MAPPING_PAGE_SIZE_DEFAULT = 50;
export const MAPPING_PAGE_SIZE_MAX = 100;
/** Polling interval of the author section while a refresh is queued or running. */
export const SUGGESTION_POLL_MS = 3000;

// --- suggestion DTOs ---------------------------------------------------------------------

export const SUGGESTION_LANGS = ["de", "en"] as const;
export type SuggestionLang = (typeof SUGGESTION_LANGS)[number];

/** Which authors a suggestion list covers (personalize-author-suggestions): the user's own or all. */
export const SUGGESTION_SCOPES = ["mine", "all"] as const;
export type SuggestionScope = (typeof SUGGESTION_SCOPES)[number];

export interface SuggestionDto {
  id: string;
  author: { id: string; name: string };
  /** Title of the shown edition (UI language if available, else the original). */
  title: string;
  subtitle: string | null;
  /** ISO date, `YYYY-MM` or `YYYY` of the shown edition. */
  publishedDate: string | null;
  year: number | null;
  /** BCP 47 primary tags of all editions. */
  languages: string[];
  series: { name: string; index: number | null } | null;
  badges: SuggestionBadge[];
  announced: boolean;
  /** Shelflist cover preview path, or null. */
  coverUrl: string | null;
  source: WorkSource;
  sourceUrl: string;
  hidden: boolean;
}

export interface SuggestionListDto {
  items: SuggestionDto[];
  total: number;
  page: number;
  pageSize: number;
  languages: { available: string[]; default: string[] };
  /** `applied`: the scope the list used (explicit, implied by the author filter, or the default); `myAuthors`: size of "My authors" (always set). */
  scope: { applied: SuggestionScope; myAuthors: number };
}

/** One author offered by the author filter of the Suggestions page. */
export interface SuggestionAuthorOptionDto {
  id: string;
  name: string;
  /** Visible suggestions of the author under the applied scope and the other filters. */
  count: number;
}

/** `GET /api/suggestions/authors`. */
export interface SuggestionAuthorsDto {
  /** Authors with at least one visible suggestion, by `count` descending then sort name, at most `limit`. */
  items: SuggestionAuthorOptionDto[];
  /** Matching authors before `limit`. */
  total: number;
  /** The selected authors that exist (independent of `q` and `limit`); `mine`: among the user's authors. */
  selected: (SuggestionAuthorOptionDto & { mine: boolean })[];
  scope: { applied: SuggestionScope };
}

export interface AuthorSuggestionsDto {
  items: SuggestionDto[];
  refresh: { state: RefreshState; jobId: string | null };
  /** ISO time of the last completed check, or null. */
  lastCheckedAt: string | null;
  outcome: SuggestionOutcome | null;
  /** ISO time when a refresh is accepted again; null when not in cooldown. */
  cooldownUntil: string | null;
  /** Suggestions enabled, lookups available, author not excluded. */
  canRefresh: boolean;
}

export interface RefreshResponseDto {
  jobId: string;
  state: Exclude<RefreshState, "idle">;
}

// --- query schemas ---------------------------------------------------------------------

const page = z.coerce
  .number({ error: "invalid" })
  .int({ error: "invalid" })
  .min(1, { error: "out_of_range" })
  .max(1_000_000, { error: "out_of_range" })
  .default(1);

const queryBoolean = z
  .enum(["true", "false"], { error: "invalid" })
  .transform((value) => value === "true");

const languageTag = languageTagSchema;

export const suggestionLangSchema = z.enum(SUGGESTION_LANGS, { error: "invalid" });

/** `author` and the deprecated `authorId` as they arrive (before merging). */
const authorValue = z.union([entityId, z.array(entityId)]);

/** Filters shared by `GET /api/suggestions` and `GET /api/suggestions/authors` (without paging). */
const suggestionFilterShape = {
  newOnly: queryBoolean.optional(),
  seriesGapsOnly: queryBoolean.optional(),
  language: z
    .union([languageTag, z.array(languageTag)])
    .transform((value) => [...new Set(Array.isArray(value) ? value : [value])])
    .pipe(z.array(languageTag).max(20, { error: "too_many" }))
    .optional(),
  /** Repeatable author ids; `authorId` (deprecated) is merged into it by the schema. */
  author: authorValue.optional(),
  authorId: entityId.optional(),
  hidden: z.enum(["only"], { error: "invalid" }).optional(),
  scope: z.enum(SUGGESTION_SCOPES, { error: "invalid" }).optional(),
  lang: suggestionLangSchema.optional(),
};

/**
 * Merges the deprecated `authorId` into `author` (deduplicated, `authorId` last) and applies the
 * cap of {@link MAX_FILTER_VALUES} to the result. Without any value `author` stays undefined.
 */
function mergeAuthors<T extends { author?: string | string[]; authorId?: string }>(
  value: T,
  ctx: z.RefinementCtx,
): Omit<T, "author" | "authorId"> & { author?: string[] } {
  const { author, authorId, ...rest } = value;
  const ids = [
    ...new Set([...(author === undefined ? [] : [author].flat()), ...(authorId ? [authorId] : [])]),
  ];
  if (ids.length > MAX_FILTER_VALUES) {
    ctx.addIssue({ code: "custom", path: ["author"], message: "too_many" });
    return z.NEVER;
  }
  return { ...rest, ...(ids.length > 0 ? { author: ids } : {}) };
}

/**
 * Query of `GET /api/suggestions`. `language` and `author` may repeat; one value arrives as a
 * string. The output has no `authorId`: it is part of `author`.
 */
export const suggestionListQuerySchema = z
  .object({
    page,
    pageSize: z.coerce
      .number({ error: "invalid" })
      .int({ error: "invalid" })
      .min(1, { error: "out_of_range" })
      .max(SUGGESTION_PAGE_SIZE_MAX, { error: "out_of_range" })
      .default(SUGGESTION_PAGE_SIZE_DEFAULT),
    ...suggestionFilterShape,
  })
  .transform(mergeAuthors);
export type SuggestionListQuery = z.infer<typeof suggestionListQuerySchema>;

export const SUGGESTION_AUTHORS_LIMIT_DEFAULT = 50;
export const SUGGESTION_AUTHORS_LIMIT_MAX = 100;
export const SUGGESTION_AUTHORS_SEARCH_MAX = 100;

/** Query of `GET /api/suggestions/authors`: the list filters without paging, plus `q` and `limit`. */
export const suggestionAuthorsQuerySchema = z
  .object({
    ...suggestionFilterShape,
    /** Word-prefix search; empty or whitespace-only means no search. */
    q: z
      .string({ error: "invalid" })
      .trim()
      .max(SUGGESTION_AUTHORS_SEARCH_MAX, { error: "invalid" })
      .transform((value) => value || undefined)
      .optional(),
    limit: z.coerce
      .number({ error: "invalid" })
      .int({ error: "invalid" })
      .min(1, { error: "out_of_range" })
      .max(SUGGESTION_AUTHORS_LIMIT_MAX, { error: "out_of_range" })
      .default(SUGGESTION_AUTHORS_LIMIT_DEFAULT),
  })
  .transform(mergeAuthors);
export type SuggestionAuthorsQuery = z.infer<typeof suggestionAuthorsQuerySchema>;

/** Query of `GET /api/authors/:id/suggestions`. */
export const authorSuggestionsQuerySchema = z.object({ lang: suggestionLangSchema.optional() });
export type AuthorSuggestionsQuery = z.infer<typeof authorSuggestionsQuerySchema>;

// --- author mapping (admin) ----------------------------------------------------------------

export interface MappingCandidateDto {
  externalId: string;
  name: string;
  lifeDates: string | null;
  workCount: number | null;
  foundBy: "isbn" | "name";
  isbnOverlap: number;
  titleOverlap: number;
  overlapTitles: string[];
  /** The candidate's normalized name equals the author's. */
  nameMatch: boolean;
  /** Confidence 0-100 that the candidate is the author. */
  score: number;
  rejected: boolean;
  url: string;
}

export interface MappingSourceDto {
  source: IdentitySource;
  status: MappingStatus;
  origin: MappingOrigin | null;
  ids: { id: string; url: string }[];
  checkedAt: string | null;
  decidedAt: string | null;
  decidedBy: { id: string; name: string } | null;
  candidates: MappingCandidateDto[];
}

export interface AuthorMappingRowDto {
  authorId: string;
  name: string;
  bookCount: number;
  mappings: Record<IdentitySource, { status: MappingStatus; ids: string[] }>;
  lastCheckedAt: string | null;
  lastOutcome: SuggestionOutcome | null;
  due: boolean;
}

export interface AuthorMappingListDto {
  items: AuthorMappingRowDto[];
  total: number;
  page: number;
  pageSize: number;
  /** Authors with at least one source in that status. */
  statusCounts: Record<MappingStatus, number>;
  /** Open mapping suggestions (author and source pairs). */
  suggestionCount: number;
}

/** An open mapping suggestion: the best non-rejected candidate of an `unclear` mapping. */
export interface MappingSuggestionDto {
  authorId: string;
  authorName: string;
  bookCount: number;
  source: IdentitySource;
  best: MappingCandidateDto;
  /** Other non-rejected candidates, best first. */
  alternatives: MappingCandidateDto[];
}

export interface MappingSuggestionListDto {
  items: MappingSuggestionDto[];
  total: number;
  page: number;
  pageSize: number;
}

export interface MappingSuggestionDecisionResponseDto {
  mapping: MappingSourceDto;
  /** Refresh job enqueued by an accepted suggestion; null when lookups are unavailable or on reject. */
  jobId: string | null;
}

export interface AcceptAllResponseDto {
  accepted: number;
  /** The sync that checks the accepted authors first (new or reused); null when none was started (feature or lookups off, nothing accepted). */
  jobId: string | null;
}

/**
 * Response of `POST /api/admin/author-mapping/sync` (HTTP 202 with a job id, 200 with `jobId: null`
 * and `authors: 0` when nothing needs a sync) and of the deprecated `/automap` and `/recheck-all`.
 */
export interface StartSyncResponseDto {
  jobId: string | null;
  authors: number;
}

export interface AuthorMappingDetailDto {
  author: { id: string; name: string; bookCount: number };
  lastCheckedAt: string | null;
  lastOutcome: SuggestionOutcome | null;
  sources: MappingSourceDto[];
}

export interface MappingDecisionResponseDto {
  mapping: MappingSourceDto;
  /** Refresh job enqueued by the decision; null when lookups are unavailable. */
  jobId: string | null;
}

// --- author sync (admin) -----------------------------------------------------------------

/** Authors that need a sync, by the first matching reason (they add up to `total`). */
export interface SyncNeededDto {
  total: number;
  /** A mapping decided by an admin after the last completed check. */
  decided: number;
  neverChecked: number;
  /** New library evidence for an author with an `unclear` or `none` non-manual source. */
  evidence: number;
  due: number;
}

export type SyncUnavailableReason = "suggestions_disabled" | "lookups_disabled";

export interface AuthorSyncActiveDto {
  jobId: string;
  status: "queued" | "running";
  processed: number;
  total: number | null;
  currentAuthor?: string;
  totals: Partial<SyncTotals>;
  /** Seconds left; only after {@link SYNC_ETA_MIN_AUTHORS} checked authors. */
  etaSeconds?: number;
}

export interface AuthorSyncLastFinishedDto {
  jobId: string;
  status: "succeeded" | "failed" | "cancelled";
  /** ISO time. */
  finishedAt: string;
  result: SyncJobResult | null;
  stoppedBy?: RunStoppedBy;
}

/** `GET /api/admin/author-mapping/sync`: computed from stored data only, no outgoing request. */
export interface AuthorSyncStatusDto {
  available: boolean;
  unavailableReason?: SyncUnavailableReason;
  needed: SyncNeededDto;
  /** Authors a sync with `all: true` would select (every author that is not excluded). */
  recheckAllCount: number;
  active?: AuthorSyncActiveDto;
  lastFinished?: AuthorSyncLastFinishedDto;
  sweep: {
    running: boolean;
    /** ISO time; set while the sweep is paused after a provider quota error. */
    pausedUntil?: string;
    /** ISO time of the earliest next check of a non-excluded author. */
    nextCheckAt?: string;
  };
}

/** Body of `POST /api/admin/author-mapping/sync`. */
export const startSyncSchema = z.object({
  all: z.boolean({ error: "invalid" }).default(false),
});
export type StartSync = z.infer<typeof startSyncSchema>;

// --- suggestion languages (instance and user) ------------------------------------------------

/** The languages the instance allows (`authorSuggestions.*`), resolved. */
export interface AllowedSuggestionLanguagesDto {
  /** BCP 47 primary tags; for "automatic" the library languages with >= 3 books, else `de`, `en`. */
  languages: string[];
  /** Editions without language information (`und`) are allowed. */
  includeUnknown: boolean;
  /** `authorSuggestions.languages` is `null`. */
  automatic: boolean;
}

/** `GET`/`PUT /api/me/suggestion-preferences`. */
export interface SuggestionPreferencesDto {
  /** The user's choice; `null`: all allowed languages. */
  languages: string[] | null;
  /** `true`/`false`, or `null`: as the instance allows. */
  includeUnknown: boolean | null;
  allowed: AllowedSuggestionLanguagesDto;
  /** The user's default view; `null`: not chosen (applies as `mine`). */
  defaultScope: SuggestionScope | null;
  /** What applies to this user: chosen languages that are still allowed (all allowed when none remain), and the default scope. */
  effective: { languages: string[]; includeUnknown: boolean; scope: SuggestionScope };
}

/**
 * Body of `PUT /api/me/suggestion-preferences`; membership in the allowed set is checked by the
 * server. Partial: only the fields present are changed (`null` resets a field), at least one is required.
 */
export const suggestionPreferencesSchema = z
  .object({
    languages: languageListSchema.nullable().optional(),
    includeUnknown: z.boolean({ error: "invalid" }).nullable().optional(),
    defaultScope: z.enum(SUGGESTION_SCOPES, { error: "invalid" }).nullable().optional(),
  })
  .refine(
    (value) =>
      value.languages !== undefined ||
      value.includeUnknown !== undefined ||
      value.defaultScope !== undefined,
    { error: "required" },
  );
export type SuggestionPreferencesInput = z.infer<typeof suggestionPreferencesSchema>;

export const mappingListQuerySchema = z.object({
  status: z.enum(MAPPING_STATUSES, { error: "invalid" }).optional(),
  q: z.string({ error: "invalid" }).max(100, { error: "too_long" }).optional(),
  page,
  pageSize: z.coerce
    .number({ error: "invalid" })
    .int({ error: "invalid" })
    .min(1, { error: "out_of_range" })
    .max(MAPPING_PAGE_SIZE_MAX, { error: "out_of_range" })
    .default(MAPPING_PAGE_SIZE_DEFAULT),
});
export type MappingListQuery = z.infer<typeof mappingListQuerySchema>;

export const mappingSuggestionListQuerySchema = z.object({
  source: z.enum(IDENTITY_SOURCES, { error: "invalid" }).optional(),
  /** Only suggestions whose best candidate has at least this confidence. */
  minConfidence: z.coerce
    .number({ error: "invalid" })
    .int({ error: "invalid" })
    .min(0, { error: "out_of_range" })
    .max(100, { error: "out_of_range" })
    .optional(),
  page,
  pageSize: mappingListQuerySchema.shape.pageSize,
});
export type MappingSuggestionListQuery = z.infer<typeof mappingSuggestionListQuerySchema>;

/** Body of accept and reject: the candidate to act on; default is the best one. */
export const mappingSuggestionActionSchema = z.object({
  externalId: z
    .string({ error: "invalid" })
    .min(1, { error: "required" })
    .max(64, { error: "too_long" })
    .optional(),
});
export type MappingSuggestionAction = z.infer<typeof mappingSuggestionActionSchema>;

/** Body of the bulk accept. */
export const acceptAllSchema = z.object({
  minConfidence: z
    .number({ error: "invalid" })
    .int({ error: "invalid" })
    .min(MAPPING_SUGGESTION_MIN_SCORE, { error: "out_of_range" })
    .max(100, { error: "out_of_range" })
    .default(MAPPING_ACCEPT_DEFAULT_CONFIDENCE),
});
export type AcceptAll = z.infer<typeof acceptAllSchema>;

export const identitySourceSchema = z.enum(IDENTITY_SOURCES, { error: "invalid" });

/**
 * Body of `PUT /api/admin/author-mapping/:authorId/:source`. The id format depends on the source
 * and is checked by the service (`author_mapping_id_invalid`), not here.
 */
export const mappingDecisionSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("ids"),
    ids: z
      .array(z.string({ error: "invalid" }).max(64, { error: "too_long" }), { error: "invalid" })
      .min(1, { error: "required" })
      .max(MAX_MAPPING_IDS, { error: "too_many" }),
  }),
  z.object({ mode: z.literal("none") }),
  z.object({ mode: z.literal("auto") }),
]);
export type MappingDecision = z.infer<typeof mappingDecisionSchema>;

/** Cover provider values a suggestion can carry. */
export type SuggestionCoverProvider = MetadataProviderId;

// --- mark as read (mark-suggestion-read) ---------------------------------------------------

/** How long after marking a suggestion as read the user can still undo it. */
export const SUGGESTION_READ_UNDO_WINDOW_MS = 30 * 60 * 1000;
/** Marks per user and minute (`rate_limited` beyond). */
export const SUGGESTION_READ_RATE_LIMIT = 30;
/** Undo records older than this are pruned by the daily maintenance. */
export const SUGGESTION_READ_MARK_RETENTION_MS = DAY_MS;

/**
 * Body of `POST /api/suggestions/:id/read`. `finishedAt` defaults to today on the server and must
 * not be in the future (`future_date`); `stars` is a whole number 1-5.
 */
export const markSuggestionReadSchema = z
  .strictObject({
    finishedAt: z.iso.date({ error: "invalid_date" }).optional(),
    /** UI language the card was shown in; picks the edition of a created book (default: stored locale). */
    lang: suggestionLangSchema.optional(),
    stars: z
      .number({ error: "invalid" })
      .int({ error: "invalid" })
      .min(RATING_MIN_STARS, { error: "out_of_range" })
      .max(RATING_MAX_STARS, { error: "out_of_range" })
      .optional(),
  })
  .superRefine((value, ctx) => {
    if (value.finishedAt === undefined) return;
    const fields = validateSessionDates(
      { finishedAt: value.finishedAt },
      { requireAnyDate: false },
    );
    if (fields.finishedAt) {
      ctx.addIssue({ code: "custom", message: fields.finishedAt, path: ["finishedAt"] });
    }
  });
export type MarkSuggestionReadInput = z.input<typeof markSuggestionReadSchema>;

/** Response of `POST /api/suggestions/:id/read` (HTTP 201). `markId` is the undo handle. */
export interface MarkSuggestionReadDto {
  markId: string;
  bookId: string;
  sessionId: string;
  /** `false`: an existing library book was reused. */
  bookCreated: boolean;
  /** `false`: no `stars` given, or the user already rated the book. */
  ratingCreated: boolean;
}
